import { Connection, PublicKey } from "@solana/web3.js";
import {
  ApiCpmmConfigInfo,
  CREATE_CPMM_POOL_AUTH,
  CREATE_CPMM_POOL_FEE_ACC,
  CREATE_CPMM_POOL_PROGRAM,
  CpmmConfigInfoLayout,
  DEVNET_PROGRAM_ID,
  LOCK_CPMM_AUTH,
  LOCK_CPMM_PROGRAM,
  getCpmmPdaAmmConfigId,
} from "@raydium-io/raydium-sdk-v2";
import { Cluster } from "./cluster";

export interface CpmmProgramSet {
  programId: PublicKey;
  poolFeeAccount: PublicKey;
  authority: PublicKey;
}

/**
 * Program addresses come from the SDK constants, never from a literal typed
 * into this repo. A wrong program id would silently create a pool nobody trades.
 */
export function cpmmPrograms(cluster: Cluster): CpmmProgramSet {
  if (cluster === "devnet") {
    return {
      programId: DEVNET_PROGRAM_ID.CREATE_CPMM_POOL_PROGRAM,
      poolFeeAccount: DEVNET_PROGRAM_ID.CREATE_CPMM_POOL_FEE_ACC,
      authority: DEVNET_PROGRAM_ID.CREATE_CPMM_POOL_AUTH,
    };
  }
  return {
    programId: CREATE_CPMM_POOL_PROGRAM,
    poolFeeAccount: CREATE_CPMM_POOL_FEE_ACC,
    authority: CREATE_CPMM_POOL_AUTH,
  };
}

export interface CpmmLockProgramSet {
  programId: PublicKey;
  authProgram: PublicKey;
}

/**
 * Burn & Earn locker addresses.
 *
 * These have to be passed explicitly. The SDK's `lockLp` / `harvestLockLp`
 * default their `programId` and `authProgram` to the MAINNET locker even when
 * the Raydium instance was loaded with `cluster: "devnet"` — verified against a
 * local validator: a devnet-loaded lock transaction referenced
 * LockrWmn6K5twhz3y9w1dQERbmgSaRkfnTeTKbpofwE, which does not exist on devnet,
 * so the lock fails at simulation. Every caller must pass these.
 */
export function cpmmLockPrograms(cluster: Cluster): CpmmLockProgramSet {
  if (cluster === "devnet") {
    return {
      programId: DEVNET_PROGRAM_ID.LOCK_CPMM_PROGRAM,
      authProgram: DEVNET_PROGRAM_ID.LOCK_CPMM_AUTH,
    };
  }
  return { programId: LOCK_CPMM_PROGRAM, authProgram: LOCK_CPMM_AUTH };
}

export interface OnChainFeeTier extends ApiCpmmConfigInfo {
  tradeFeeBps: number;
  disableCreatePool: boolean;
  createPoolFeeLamports: string;
}

/**
 * Reads the CPMM AMM-config accounts straight from the chain instead of calling
 * Raydium's HTTP API.
 *
 * Why: api.getCpmmConfigs() is a mainnet endpoint. The official demo works
 * around that on devnet by keeping the mainnet fee *values* and only
 * recomputing the PDA address -- which is wrong whenever the two clusters have
 * different tiers at the same index. They do: on devnet index 1 is 0.30%,
 * on mainnet index 1 is 1.00%. Reading the account is exact on both.
 */
export async function readFeeTiers(
  conn: Connection,
  cluster: Cluster,
  maxIndex = 16
): Promise<OnChainFeeTier[]> {
  const { programId } = cpmmPrograms(cluster);
  const pdas: { index: number; pk: PublicKey }[] = [];
  for (let i = 0; i < maxIndex; i++) {
    pdas.push({ index: i, pk: getCpmmPdaAmmConfigId(programId, i).publicKey });
  }
  const infos = await conn.getMultipleAccountsInfo(
    pdas.map((p) => p.pk),
    "confirmed"
  );

  const tiers: OnChainFeeTier[] = [];
  infos.forEach((info, i) => {
    if (!info) return;
    if (!info.owner.equals(programId)) return;
    const d = CpmmConfigInfoLayout.decode(info.data);
    const tradeFeeRate = Number(d.tradeFeeRate.toString());
    tiers.push({
      id: pdas[i].pk.toBase58(),
      index: Number(d.index.toString()),
      protocolFeeRate: Number(d.protocolFeeRate.toString()),
      tradeFeeRate,
      fundFeeRate: Number(d.fundFeeRate.toString()),
      createPoolFee: d.createPoolFee.toString(),
      creatorFeeRate: d.creatorFeeRate ? Number(d.creatorFeeRate.toString()) : 0,
      tradeFeeBps: tradeFeeRate / 100, // rate denominator is 1e6
      disableCreatePool: Boolean(d.disableCreatePool),
      createPoolFeeLamports: d.createPoolFee.toString(),
    });
  });
  return tiers;
}

/** Rate denominator used by the CPMM program for every *FeeRate field. */
export const FEE_RATE_DENOMINATOR = 1_000_000;

/**
 * Formats a 1e6-denominated rate as an exact percentage string.
 * Integer arithmetic on purpose: 3000/1e6*100 renders as
 * "0.30000000000000004%" in IEEE-754, which looks like a bug in a launch plan.
 */
export function pct(rate: number): string {
  if (!Number.isInteger(rate) || rate < 0) throw new Error(`invalid rate: ${rate}`);
  const s = String(rate).padStart(5, "0");
  const whole = s.slice(0, s.length - 4);
  const frac = s.slice(s.length - 4).replace(/0+$/, "");
  return frac ? `${whole}.${frac}%` : `${whole}%`;
}

/**
 * Upper bound on what a trader pays: trade fee plus creator fee.
 *
 * The creator fee is charged against the swap amount, not against the trade fee
 * (CurveCalculator.creatorFee is ceil(amount * rate / 1e6), the same shape as
 * the trading fee), so the two add rather than nest.
 *
 * It is an UPPER bound, not the actual cost, because the cp-swap program only
 * collects the creator fee when the pool's enableCreatorFee flag is set. A pool
 * created with createPool (what this launcher does) has enableCreatorFee=false,
 * so only the trade fee applies. Verified on-chain 2026-09-04: pool state showed
 * enableCreatorFee=false and creatorFeesMintA/B=0 after a live swap, and the
 * wallet received the full no-creator-fee amount.
 *
 * Note that the SDK's own computeSwapAmount() does NOT check that flag and
 * always subtracts the creator fee, so its quotes run low for these pools.
 */
export function totalTraderCostPct(t: OnChainFeeTier): string {
  return pct(t.tradeFeeRate + t.creatorFeeRate);
}

export function describeTier(t: OnChainFeeTier): string {
  return (
    `index ${t.index}: trade ${pct(t.tradeFeeRate)}, creator ${pct(t.creatorFeeRate)} ` +
    `(only if enabled), trader pays ${pct(t.tradeFeeRate)}..${totalTraderCostPct(t)}  [${t.id}]`
  );
}

/**
 * Selects by economic meaning, never by array position -- and never by trade
 * fee alone, because that does not identify a tier.
 */
export function selectFeeTier(
  tiers: OnChainFeeTier[],
  want: { tradeFeeBps: number; creatorFeeRate: number; feeTierAccount?: string | null }
): OnChainFeeTier {
  if (want.feeTierAccount) {
    const pinned = tiers.find((t) => t.id === want.feeTierAccount);
    if (!pinned) {
      throw new Error(
        `feeTierAccount ${want.feeTierAccount} is not a CPMM AMM-config account on this cluster`
      );
    }
    if (pinned.tradeFeeBps !== want.tradeFeeBps || pinned.creatorFeeRate !== want.creatorFeeRate) {
      throw new Error(
        `feeTierAccount ${want.feeTierAccount} disagrees with the configured rates: ` +
          `account has trade ${pinned.tradeFeeBps}bps / creator ${pinned.creatorFeeRate}, ` +
          `config says ${want.tradeFeeBps}bps / ${want.creatorFeeRate}`
      );
    }
    if (pinned.disableCreatePool) {
      throw new Error(`fee tier ${pinned.id} has pool creation disabled`);
    }
    return pinned;
  }

  const match = tiers.filter(
    (t) => t.tradeFeeBps === want.tradeFeeBps && t.creatorFeeRate === want.creatorFeeRate
  );
  if (match.length === 0) {
    throw new Error(
      `no CPMM fee tier on this cluster has tradeFeeBps=${want.tradeFeeBps} and ` +
        `creatorFeeRate=${want.creatorFeeRate}.\nAvailable tiers:\n` +
        tiers.map((t) => `  ${describeTier(t)}`).join("\n")
    );
  }
  if (match.length > 1) {
    throw new Error(
      `ambiguous fee tier: ${match.length} accounts match. Pin one with "feeTierAccount":\n` +
        match.map((t) => `  ${describeTier(t)}`).join("\n")
    );
  }
  const tier = match[0];
  if (tier.disableCreatePool) {
    throw new Error(`fee tier ${describeTier(tier)} has pool creation disabled`);
  }
  return tier;
}
